import { Document, Page, Text, View, StyleSheet, Font, Svg, Path, Line } from "@react-pdf/renderer";

// ----- Fonts (Google Fonts static hosts, TTF) -----
// Playfair Display (serif) — used for the recipient name & certification title
Font.register({
  family: "PlayfairDisplay",
  fonts: [
    { src: "https://fonts.gstatic.com/s/playfairdisplay/v40/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKdFvUDQ.ttf", fontWeight: 400 },
    { src: "https://fonts.gstatic.com/s/playfairdisplay/v40/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKeiukDQ.ttf", fontWeight: 700 },
    { src: "https://fonts.gstatic.com/s/playfairdisplay/v40/nuFRD-vYSZviVYUb_rj3ij__anPXDTnCjmHKM4nYO7KN_qiTbtY.ttf", fontWeight: 400, fontStyle: "italic" },
    { src: "https://fonts.gstatic.com/s/playfairdisplay/v40/nuFRD-vYSZviVYUb_rj3ij__anPXDTnCjmHKM4nYO7KN_k-UbtY.ttf", fontWeight: 700, fontStyle: "italic" },
  ],
});

// Inter (sans-serif) — used for meta / body / small labels
Font.register({
  family: "Inter",
  fonts: [
    { src: "https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfMZg.ttf", fontWeight: 400 },
    { src: "https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuI6fMZg.ttf", fontWeight: 500 },
    { src: "https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuFuYMZg.ttf", fontWeight: 700 },
  ],
});

// Disable hyphenation to keep names/titles intact
Font.registerHyphenationCallback((w) => [w]);

// ----- Color palette (Aurum / sober) -----
const COLORS = {
  paper: "#FDFDFB",
  ink: "#1B1B1D",
  ink2: "#4A4A4E",
  muted: "#8A8A8F",
  amber: "#B08B3F", // sober amber accent
  amberSoft: "#DDBB78",
  hair: "#E8E4DA",
};

// Fixed A4 landscape dimensions: 842 x 595 pt (595.28x841.89)
const styles = StyleSheet.create({
  page: {
    backgroundColor: COLORS.paper,
    fontFamily: "Inter",
    color: COLORS.ink,
    padding: 0,
    position: "relative",
  },
  // Outer decorative frame (thin amber border with generous inner padding)
  frameOuter: {
    position: "absolute",
    top: 22,
    left: 22,
    right: 22,
    bottom: 22,
    borderWidth: 1.2,
    borderColor: COLORS.amber,
    borderStyle: "solid",
  },
  frameInner: {
    position: "absolute",
    top: 30,
    left: 30,
    right: 30,
    bottom: 30,
    borderWidth: 0.4,
    borderColor: COLORS.amberSoft,
    borderStyle: "solid",
  },
  // Content container — fixed insets, no % values
  content: {
    position: "absolute",
    top: 60,
    left: 70,
    right: 70,
    bottom: 60,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
  },
  // Header
  header: {
    width: "100%",
    alignItems: "center",
    marginTop: 6,
  },
  brandRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 6,
  },
  brandDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: COLORS.amber,
    marginHorizontal: 6,
  },
  brandName: {
    fontFamily: "PlayfairDisplay",
    fontWeight: 700,
    fontSize: 15,
    color: COLORS.ink,
    letterSpacing: 3,
  },
  brandTag: {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: 7,
    color: COLORS.muted,
    letterSpacing: 3,
    textTransform: "uppercase",
    marginTop: 6,
  },
  hairline: {
    width: 60,
    height: 0.6,
    backgroundColor: COLORS.amber,
    marginVertical: 14,
  },
  kicker: {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: 9,
    color: COLORS.amber,
    letterSpacing: 5,
    textTransform: "uppercase",
  },
  // Center — recipient
  centerBlock: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 1,
    paddingHorizontal: 20,
  },
  awardedTo: {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: 10,
    color: COLORS.ink2,
    letterSpacing: 4,
    textTransform: "uppercase",
    marginBottom: 16,
  },
  recipientName: {
    fontFamily: "PlayfairDisplay",
    fontWeight: 700,
    color: COLORS.ink,
    textAlign: "center",
    // Size stays fixed — react-pdf will wrap long names onto multiple lines
    fontSize: 38,
    lineHeight: 1.1,
    marginBottom: 4,
  },
  // Certification title
  certTitleLabel: {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: 9,
    color: COLORS.muted,
    letterSpacing: 3,
    textTransform: "uppercase",
    marginTop: 22,
    marginBottom: 8,
  },
  certTitle: {
    fontFamily: "PlayfairDisplay",
    fontWeight: 400,
    fontSize: 20,
    color: COLORS.ink,
    textAlign: "center",
    fontStyle: "italic",
    maxWidth: 620,
    lineHeight: 1.25,
  },
  mention: {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: 10,
    color: COLORS.ink2,
    textAlign: "center",
    marginTop: 14,
    maxWidth: 560,
    lineHeight: 1.5,
  },
  // Footer row
  footer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 20,
  },
  footerCol: {
    width: 200,
  },
  footerColRight: {
    width: 200,
    alignItems: "flex-end",
  },
  footerLabel: {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: 7.5,
    color: COLORS.muted,
    letterSpacing: 2.5,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  footerValue: {
    fontFamily: "PlayfairDisplay",
    fontWeight: 400,
    fontSize: 12,
    color: COLORS.ink,
  },
  signatureLine: {
    width: 160,
    height: 0.6,
    backgroundColor: COLORS.ink,
    marginBottom: 4,
  },
  signatureName: {
    fontFamily: "PlayfairDisplay",
    fontWeight: 700,
    fontSize: 11,
    color: COLORS.ink,
    letterSpacing: 1,
  },
  signatureRole: {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: 7.5,
    color: COLORS.muted,
    letterSpacing: 2,
    textTransform: "uppercase",
    marginTop: 2,
  },
  certIdRow: {
    position: "absolute",
    left: 70,
    right: 70,
    bottom: 34,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  certIdLabel: {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: 7,
    color: COLORS.muted,
    letterSpacing: 2,
    textTransform: "uppercase",
  },
  certIdValue: {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: 7.5,
    color: COLORS.ink2,
    letterSpacing: 1.5,
    marginLeft: 6,
  },
});

// Small vector ornament (thin serif flourish) — kept minimal
function Flourish() {
  return (
    <Svg width={110} height={10} viewBox="0 0 110 10" style={{ marginTop: 6 }}>
      <Line x1="0" y1="5" x2="42" y2="5" stroke={COLORS.amberSoft} strokeWidth="0.6" />
      <Path d="M46 5 L55 1 L64 5 L55 9 Z" fill={COLORS.amber} />
      <Line x1="68" y1="5" x2="110" y2="5" stroke={COLORS.amberSoft} strokeWidth="0.6" />
    </Svg>
  );
}

export function formatFrenchDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

/**
 * CertificateDocument
 * Props:
 *  - userName            (string) — full name from the connected account
 *  - certificationTitle  (string) — title of the certification actually completed
 *  - date                (string, ISO) — issue date (defaults to today for dev preview)
 *  - certificateId       (string) — unique certificate code
 */
export default function CertificateDocument({
  userName = "Prénom Nom",
  certificationTitle = "Intitulé de la certification",
  date = new Date().toISOString(),
  certificateId = "AURUM-000000",
}) {
  const dateFr = formatFrenchDate(date);
  return (
    <Document title={`Certificat Aurum Academy - ${userName}`} author="Aurum Academy">
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.frameOuter} />
        <View style={styles.frameInner} />

        <View style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.brandRow}>
              <View style={styles.brandDot} />
              <Text style={styles.brandName}>AURUM ACADEMY</Text>
              <View style={styles.brandDot} />
            </View>
            <Text style={styles.brandTag}>Education · Certification · School</Text>
            <View style={styles.hairline} />
            <Text style={styles.kicker}>Certificat de réussite</Text>
          </View>

          {/* Center */}
          <View style={styles.centerBlock}>
            <Text style={styles.awardedTo}>Décerné à</Text>
            <Text style={styles.recipientName}>{userName}</Text>
            <Flourish />

            <Text style={styles.certTitleLabel}>a complété avec succès le parcours</Text>
            <Text style={styles.certTitle}>« {certificationTitle} »</Text>

            <Text style={styles.mention}>
              en satisfaisant à l'ensemble des évaluations requises par Aurum Academy.
            </Text>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <View style={styles.footerCol}>
              <Text style={styles.footerLabel}>Date d'obtention</Text>
              <Text style={styles.footerValue}>{dateFr}</Text>
            </View>
            <View style={styles.footerColRight}>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureName}>Aurum Academy</Text>
              <Text style={styles.signatureRole}>Autorité de certification</Text>
            </View>
          </View>
        </View>

        {/* Certificate ID — discreet, bottom center */}
        <View style={styles.certIdRow}>
          <Text style={styles.certIdLabel}>Numéro de certificat</Text>
          <Text style={styles.certIdValue}>{certificateId}</Text>
        </View>
      </Page>
    </Document>
  );
}
